import { useCallback, useSyncExternalStore } from "react";
import { useSession } from "../auth/useSession";
import { scopedBrowserStorageKey } from "./scopedBrowserStorage";

/**
 * Der aktive Warenkorb (Beta-Feedback 2026-10-08): Wer im Warenkorb einen
 * offenen Korb auswählt, legt Artikel überall in der App dort ab, bis ein
 * anderer gewählt wird. Je Person und Mandant im Browser gemerkt; ist der
 * gemerkte Korb nicht mehr offen, gilt der zuletzt bearbeitete.
 */
const NAMESPACE = "truucost.active-cart.v1";
const CHANGE_EVENT = "truucost:active-cart";
const fallbackChoices = new Map<string, string | null>();

function read(key: string | null): string | null {
  if (!key) return null;
  if (fallbackChoices.has(key)) return fallbackChoices.get(key) ?? null;
  try {
    const value = localStorage.getItem(key);
    return value && value.length <= 64 ? value : null;
  } catch {
    return null;
  }
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function useActiveCartId(): [string | null, (cartId: string | null, expected?: string | null) => void] {
  const { user } = useSession();
  const key = scopedBrowserStorageKey(NAMESPACE, user);
  const cartId = useSyncExternalStore(subscribe, () => read(key), () => null);
  const setCartId = useCallback(
    (next: string | null, expected?: string | null) => {
      if (!key || read(key) === next) return;
      // A completed request must not replace a choice made while it was pending.
      if (expected !== undefined && read(key) !== expected) return;
      try {
        if (next) localStorage.setItem(key, next);
        else localStorage.removeItem(key);
        fallbackChoices.delete(key);
      } catch {
        // Preserve the explicit write destination for this app session even
        // when the browser cannot persist preferences.
        fallbackChoices.set(key, next);
      }
      window.dispatchEvent(new Event(CHANGE_EVENT));
    },
    [key],
  );
  return [cartId, setCartId];
}
